import "./styles/franchise.css";
import Nav from "./Nav";
import Footer from "./Footer";
import { useState } from "react";
import slush from "../images/icon/slushCupBlack.webp";
import SlushIcon from "../images/icon/Slush.svg";
import slushLogo from "../images/tubbees_slush_logo.webp";
import ChartUpIcon from "../images/icon/chart-up.svg";
import colorfulSlusheesOutdoor from "../images/franchise/colorful-slushees-outdoor.png";
import slushieWallWithStaff from "../images/franchise/slushie-wall-with-staff.png";
import dessertLabSlushieMachines from "../images/franchise/dessert-lab-slushie-machines.png";
import axios from "axios";
import { API_BASE_URL } from "../config";

const infoBlocks = [
  {
    icon: SlushIcon,
    title: "Bring the buzz",
    text: "Tubbees Slushee draws customers in with bold flavours and a fun, colourful brand that stands out on any high street.",
  },
  {
    icon: slushLogo,
    iconClass: "franchise-info__icon--logo",
    title: "Proven concept",
    text: "Join a growing family of Tubbees Express stores already serving happy customers. Full support and training provided to get you started.",
  },
  {
    icon: ChartUpIcon,
    title: "More footfall, better margins",
    text: "Tubbees is a name customers already know and love. Stock our slushee in your store to draw more shoppers in and enjoy strong margins on every sale.",
  },
];

const galleryImages = [
  {
    src: colorfulSlusheesOutdoor,
    alt: "Four colourful layered Tubbees slushees on a picnic table outdoors",
    label: "Colourful slushees",
  },
  {
    src: slushieWallWithStaff,
    alt: "Tubbees slushie wall with staff member in store",
    label: "Slushie wall in store",
  },
  {
    src: dessertLabSlushieMachines,
    alt: "Row of Tubbees slushie machines offering a wide range of flavours",
    label: "Wide flavour options",
  },
];

const ukPostcodeRegex =
  /^(GIR 0AA)|((([A-Z][0-9]{1,2})|(([A-Z][A-HJ-Y][0-9]{1,2})|(([A-Z][0-9][A-Z])|([A-Z][A-HJ-Y][0-9]?[A-Z])))) [0-9][A-Z]{2})$/i;

function isValidPostcode(postcode) {
  const normalized = postcode.trim().toUpperCase();
  return ukPostcodeRegex.test(normalized);
}

export default function Franchise() {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [postcode, setPostcode] = useState("");
  const [timeframe, setTimeframe] = useState("Just enquiring");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [submitBtn, setSubmitBtn] = useState(true);
  const [loader, setLoader] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleFranchise() {
    const newErrors = {};

    if (!name.trim()) newErrors.name = "Name is required";
    if (!surname.trim()) newErrors.surname = "Surname is required";
    if (!phone.trim()) newErrors.phone = "Contact number is required";
    if (!email.trim()) newErrors.email = "Email is required";

    if (!businessName.trim()) {
      newErrors.businessName = "Business name is required";
    }

    if (!businessType.trim()) {
      newErrors.businessType = "Business type is required";
    }

    if (!timeframe) {
      newErrors.timeframe = "Please select a timeframe";
    }

    if (!message.trim()) {
      newErrors.message = "Please provide more information";
    }

    if (!postcode.trim()) {
      newErrors.postcode = "Postcode is required";
    } else if (!isValidPostcode(postcode)) {
      newErrors.postcode =
        "Enter a valid UK postcode (e.g. SW1A 1AA)";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSubmitBtn(false);
    setLoader(true);

    const data = {
      name,
      surname,
      phone,
      email,
      businessName,
      businessType,
      postcode,
      timeframe,
      message,
    };

    try {
      await axios.post(`${API_BASE_URL}/tubbeesSite/franchise`, data);

      setLoader(false);
      setSent(true);

      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      console.error(err);
      setLoader(false);
      setSubmitBtn(true);
    }
  }

  const clearError = (field) => {
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  return (
    <section className="franchise">
      <Nav />

      {/* =====================================================
          HERO
          ===================================================== */}

      <header className="franchise-hero">
        <div className="franchise-hero__shape franchise-hero__shape--one" />
        <div className="franchise-hero__shape franchise-hero__shape--two" />

        <div className="franchise-hero__content">
          <span className="franchise-eyebrow">
            TUBBEES EXPRESS
          </span>

          <h1>
            Bring the <span>Tubbees buzz</span> to your store.
          </h1>

          <p className="franchise-hero__subtitle">
            Partner with Tubbees Express and bring colourful slushees,
            bold flavours and a proven customer experience to your
            business.
          </p>

          <a
            href="#franchise-contact"
            className="franchise-hero__cta"
          >
            Become a partner
            <span>↓</span>
          </a>
        </div>

        <div className="franchise-hero__visual">
          <img
            src={colorfulSlusheesOutdoor}
            alt="Colourful Tubbees slushees"
          />

          <div className="franchise-hero__badge">
            <strong>BIG</strong>
            <span>FLAVOUR</span>
          </div>
        </div>
      </header>

      {/* =====================================================
          INTRO
          ===================================================== */}

      <section className="franchise-intro">
        <div className="franchise-intro__inner">
          <span className="franchise-section-label">
            WHY TUBBEES EXPRESS?
          </span>

          <h2>
            More than a machine.
            <br />
            <span>A whole Tubbees experience.</span>
          </h2>

          <p>
            Give your customers something colourful, exciting and
            easy to come back for — while adding a fresh revenue
            stream to your business.
          </p>
        </div>
      </section>

      {/* =====================================================
          BENEFITS
          ===================================================== */}

      <section
        className="franchise-info"
        aria-label="Tubbees Express benefits"
      >
        <div className="franchise-info__grid">
          {infoBlocks.map((block, index) => (
            <article
              key={block.title}
              className={`franchise-info__card franchise-info__card--${
                index + 1
              }`}
            >
              <div className="franchise-info__number">
                0{index + 1}
              </div>

              <div className="franchise-info__icon-wrap">
                <img
                  src={block.icon}
                  alt=""
                  className={`franchise-info__icon${
                    block.iconClass
                      ? ` ${block.iconClass}`
                      : ""
                  }`}
                />
              </div>

              <h3>{block.title}</h3>

              <p>{block.text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* =====================================================
          GALLERY
          ===================================================== */}

      <section
        className="franchise-gallery"
        aria-label="Tubbees Express gallery"
      >
        <div className="franchise-gallery__heading">
          <div>
            <span className="franchise-section-label">
              SEE IT IN ACTION
            </span>

            <h2>
              Make your space
              <br />
              <span>stand out.</span>
            </h2>
          </div>

          <p>
            From the machine to the branding, Tubbees Express is
            designed to turn heads and give customers another reason
            to visit.
          </p>
        </div>

        <div className="franchise-gallery__grid">
          {galleryImages.map((item, index) => (
            <figure
              key={item.label}
              className={`franchise-gallery__tile franchise-gallery__tile--${
                index + 1
              }`}
            >
              <img
                src={item.src}
                alt={item.alt}
                className="franchise-gallery__image"
                loading="lazy"
              />

              <figcaption className="franchise-gallery__caption">
                <span>0{index + 1}</span>
                {item.label}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* =====================================================
          CONTACT
          ===================================================== */}

      <section
        className="franchise-contact"
        id="franchise-contact"
        aria-label="Contact form"
      >
        <div className="franchise-contact__inner">
          <div className="franchise-contact__heading">
            <span className="franchise-section-label">
              LET'S TALK
            </span>

            <h2>
              Ready to bring
              <br />
              <span>Tubbees</span> to you?
            </h2>

            <p>
              Tell us a little about your business and what you're
              looking for. Our team will get back to you with the
              next steps.
            </p>

            <div className="franchise-contact__note">
              <span>✦</span>

              <div>
                <strong>
                  Interested in Tubbees Express?
                </strong>

                <small>
                  Complete the form and our team will send you a
                  Tubbees Express pack.
                </small>
              </div>
            </div>
          </div>

          <form
            className="franchise-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleFranchise();
            }}
          >
            <div className="franchise-form__top">
              <span>01</span>

              <div>
                <strong>Your details</strong>
                <small>Tell us who you are.</small>
              </div>
            </div>

            <div className="franchise-form__row">
              <div className="franchise-form__field">
                <label htmlFor="franchise-name">
                  Name
                </label>

                <input
                  id="franchise-name"
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearError("name");
                  }}
                  autoComplete="given-name"
                  aria-invalid={!!errors.name}
                  placeholder="Your first name"
                />

                {errors.name && (
                  <span className="franchise-form__error">
                    {errors.name}
                  </span>
                )}
              </div>

              <div className="franchise-form__field">
                <label htmlFor="franchise-surname">
                  Surname
                </label>

                <input
                  id="franchise-surname"
                  type="text"
                  value={surname}
                  onChange={(e) => {
                    setSurname(e.target.value);
                    clearError("surname");
                  }}
                  autoComplete="family-name"
                  aria-invalid={!!errors.surname}
                  placeholder="Your surname"
                />

                {errors.surname && (
                  <span className="franchise-form__error">
                    {errors.surname}
                  </span>
                )}
              </div>
            </div>

            <div className="franchise-form__row">
              <div className="franchise-form__field">
                <label htmlFor="franchise-phone">
                  Contact number
                </label>

                <input
                  id="franchise-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    clearError("phone");
                  }}
                  autoComplete="tel"
                  aria-invalid={!!errors.phone}
                  placeholder="07xxx xxxxxx"
                />

                {errors.phone && (
                  <span className="franchise-form__error">
                    {errors.phone}
                  </span>
                )}
              </div>

              <div className="franchise-form__field">
                <label htmlFor="franchise-email">
                  Email
                </label>

                <input
                  id="franchise-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearError("email");
                  }}
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                  placeholder="you@example.com"
                />

                {errors.email && (
                  <span className="franchise-form__error">
                    {errors.email}
                  </span>
                )}
              </div>
            </div>

            <div className="franchise-form__divider">
              <span>02</span>

              <div>
                <strong>Your business</strong>
                <small>
                  Tell us about your location.
                </small>
              </div>
            </div>

            <div className="franchise-form__row">
              <div className="franchise-form__field">
                <label htmlFor="franchise-business-name">
                  Business name
                </label>

                <input
                  id="franchise-business-name"
                  type="text"
                  value={businessName}
                  onChange={(e) => {
                    setBusinessName(e.target.value);
                    clearError("businessName");
                  }}
                  autoComplete="organization"
                  aria-invalid={!!errors.businessName}
                  placeholder="Your business"
                />

                {errors.businessName && (
                  <span className="franchise-form__error">
                    {errors.businessName}
                  </span>
                )}
              </div>

              <div className="franchise-form__field">
                <label htmlFor="franchise-business-type">
                  Business type
                </label>

                <input
                  id="franchise-business-type"
                  type="text"
                  value={businessType}
                  onChange={(e) => {
                    setBusinessType(e.target.value);
                    clearError("businessType");
                  }}
                  autoComplete="organization-title"
                  aria-invalid={!!errors.businessType}
                  placeholder="e.g. Convenience store"
                />

                {errors.businessType && (
                  <span className="franchise-form__error">
                    {errors.businessType}
                  </span>
                )}
              </div>
            </div>

            <div className="franchise-form__field">
              <label htmlFor="franchise-postcode">
                Business postcode
              </label>

              <input
                id="franchise-postcode"
                type="text"
                value={postcode}
                minLength={5}
                maxLength={8}
                onChange={(e) => {
                  setPostcode(e.target.value);
                  clearError("postcode");
                }}
                autoComplete="postal-code"
                aria-invalid={!!errors.postcode}
                placeholder="e.g. G1 1AA"
              />

              {errors.postcode && (
                <span className="franchise-form__error">
                  {errors.postcode}
                </span>
              )}
            </div>

            <div className="franchise-form__field">
              <label htmlFor="franchise-timeframe">
                When are you looking to start?
              </label>

              <select
                id="franchise-timeframe"
                value={timeframe}
                onChange={(e) => {
                  setTimeframe(e.target.value);
                  clearError("timeframe");
                }}
                aria-invalid={!!errors.timeframe}
              >
                <option value="Just enquiring">
                  Just enquiring
                </option>

                <option value="Immediately">
                  Immediately
                </option>

                <option value="Within 1 month">
                  Within 1 month
                </option>
              </select>

              {errors.timeframe && (
                <span className="franchise-form__error">
                  {errors.timeframe}
                </span>
              )}
            </div>

            <div className="franchise-form__field">
              <label htmlFor="franchise-message">
                Tell us more
              </label>

              <textarea
                id="franchise-message"
                rows="5"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  clearError("message");
                }}
                aria-invalid={!!errors.message}
                placeholder="Tell us a little about your business, location and what you're looking for..."
              />

              {errors.message && (
                <span className="franchise-form__error">
                  {errors.message}
                </span>
              )}
            </div>

            {submitBtn && (
              <button
                type="submit"
                className="franchise-form__submit"
              >
                <span>Send enquiry</span>
                <strong>→</strong>
              </button>
            )}

            {loader && (
              <img
                src={slush}
                alt="Submitting..."
                className="franchise-form__loader"
              />
            )}

            {sent && (
              <div className="franchise-success">
                <svg
                  className="checkmark"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 52 52"
                  aria-label="Form submitted successfully"
                >
                  <circle
                    className="checkmark__circle"
                    cx="26"
                    cy="26"
                    r="25"
                    fill="none"
                  />

                  <path
                    className="checkmark__check"
                    fill="none"
                    d="M14.1 27.2l7.1 7.2 16.7-16.8"
                  />
                </svg>

                <strong>
                  Thanks for getting in touch!
                </strong>

                <span>
                  We'll be in touch shortly.
                </span>
              </div>
            )}
          </form>
        </div>
      </section>

      <Footer />
    </section>
  );
}